import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney, searchTrips } from '../../api/bookingApi';
import { useBooking } from '../../booking/BookingContext';
import { dateToYmd, todayInTanzania } from '../../utils/travelDate';

function addDays(ymd, delta) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return dateToYmd(dt);
}

function formatDisplayDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function cacheKey({ origin, destination, date, passengers }) {
  return `sf-trips:${origin}|${destination}|${date}|${passengers}`;
}

function readTripCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > 60_000) return null;
    return Array.isArray(parsed.trips) ? parsed.trips : null;
  } catch {
    return null;
  }
}

function writeTripCache(key, trips) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), trips }));
  } catch {
    /* ignore quota */
  }
}

function TripSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-pink-100 p-4 animate-pulse">
      <div className="flex justify-between">
        <div className="space-y-2">
          <div className="h-4 w-40 bg-gray-200 rounded" />
          <div className="h-3 w-24 bg-gray-100 rounded" />
        </div>
        <div className="h-6 w-20 bg-pink-100 rounded" />
      </div>
      <div className="mt-4 h-10 bg-gray-100 rounded-xl" />
    </div>
  );
}

export default function TripsPage() {
  const booking = useBooking();
  const navigate = useNavigate();
  const key = cacheKey(booking);
  const [trips, setTrips] = useState(() => readTripCache(key) || []);
  const [loading, setLoading] = useState(() => !readTripCache(key)?.length);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const reqId = useRef(0);

  const todayYmd = dateToYmd(todayInTanzania());

  const load = useCallback(async () => {
    const id = ++reqId.current;
    const k = cacheKey({
      origin: booking.origin,
      destination: booking.destination,
      date: booking.date,
      passengers: booking.passengers,
    });
    const cached = readTripCache(k);
    if (cached?.length) {
      setTrips(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setTrips([]);
    }
    setError('');
    try {
      const list = await searchTrips({
        origin: booking.origin,
        destination: booking.destination,
        date: booking.date,
        passengers: booking.passengers,
      });
      if (id !== reqId.current) return;
      setTrips(list);
      writeTripCache(k, list);
    } catch (e) {
      if (id !== reqId.current) return;
      if (!cached?.length) {
        setTrips([]);
        setError(e.message || 'Could not load trips.');
      }
    } finally {
      if (id === reqId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [booking.origin, booking.destination, booking.date, booking.passengers]);

  useEffect(() => {
    load();
    return () => {
      reqId.current += 1;
    };
  }, [load]);

  const shiftDate = (delta) => {
    const next = addDays(booking.date, delta);
    if (next < todayYmd) return;
    booking.setSearch({
      origin: booking.origin,
      destination: booking.destination,
      date: next,
      passengers: booking.passengers,
    });
  };

  const onSelect = (trip) => {
    booking.selectTrip(trip);
    navigate('/book/seats');
  };

  const originName =
    trips[0]?.originName || booking.origin.replace(/-/g, ' ');
  const destName =
    trips[0]?.destinationName || booking.destination.replace(/-/g, ' ');

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-pink-100 p-4 shadow-sm">
        <p className="text-center font-semibold text-gray-900 capitalize">
          {originName} → {destName}
        </p>
        <p className="text-center text-sm text-gray-500 mt-1">
          {booking.passengers} passenger{booking.passengers > 1 ? 's' : ''}
          {refreshing ? ' · Updating…' : ''}
        </p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => shiftDate(-1)}
            disabled={booking.date <= todayYmd || loading}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold disabled:opacity-40"
          >
            ← Prev
          </button>
          <p className="text-sm font-bold text-superfeo-pink">
            {formatDisplayDate(booking.date)}
          </p>
          <button
            type="button"
            onClick={() => shiftDate(1)}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          <TripSkeleton />
          <TripSkeleton />
          <TripSkeleton />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <p className="text-red-700 text-sm mb-3">{error}</p>
          <button
            type="button"
            onClick={load}
            className="bg-superfeo-pink text-white px-4 py-2 rounded-lg text-sm font-bold"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && trips.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
          No trips found for this date. Try another day.
        </div>
      )}

      {!loading &&
        trips.map((trip) => (
          <article
            key={trip.id}
            className="bg-white rounded-2xl border border-pink-100 p-4 shadow-sm hover:border-superfeo-pink/40 transition"
          >
            <div className="flex justify-between gap-3 items-start">
              <div>
                <p className="font-bold text-gray-900">
                  {trip.bus?.name || trip.operator || 'Superfeo Express'}
                </p>
                {(trip.safariName || trip.bus?.model) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {trip.safariName || trip.bus?.model}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {trip.seatsAvailable ?? 0} seats left
                </p>
              </div>
              <p className="text-lg font-heading font-bold text-superfeo-pink whitespace-nowrap">
                {formatMoney(trip.price, trip.currency || 'TZS')}
              </p>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <div>
                <p className="font-bold text-gray-900">{trip.departureTime}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {trip.originName}
                </p>
              </div>
              <div className="flex-1 mx-3 h-px bg-gray-200 relative">
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] text-gray-400 bg-white px-1">
                  {trip.arrivesNextDay ? '+1 day' : 'direct'}
                </span>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">
                  {trip.arrivalTime}
                  {trip.arrivesNextDay ? ' +1' : ''}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {trip.destinationName}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onSelect(trip)}
              className="mt-4 w-full bg-superfeo-pink hover:bg-superfeo-darkpink text-white font-bold py-3 rounded-xl"
            >
              Select seats
            </button>
          </article>
        ))}
    </div>
  );
}
